import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { newsletterHtmlToText, newsletterTextToHtml, sanitizeNewsletterHtml } from "../../../packages/core/src/newsletterText";

type Props = {
  value: string;
  html?: string;
  placeholder: string;
  className: string;
  style?: CSSProperties;
  onChange: (text: string, html: string) => void;
};
type Format = "bold" | "italic" | "underline";
type Toolbar = {left:number;top:number;bold:boolean;italic:boolean;underline:boolean};

/** Native editing preserves selection and undo history; stored markup has a tiny allowlist. */
export function RichTextInline({value,html,placeholder,className,style,onChange}: Props) {
  const editable = useRef<HTMLDivElement>(null);
  const range = useRef<Range | null>(null);
  const [toolbar,setToolbar] = useState<Toolbar | null>(null);
  const change = useRef(onChange);
  change.current = onChange;

  useEffect(() => {
    const element = editable.current;
    if (element && document.activeElement !== element) {
      const desired = html ? sanitizeNewsletterHtml(html) : value ? newsletterTextToHtml(value) : "";
      if (element.innerHTML !== desired) element.innerHTML = desired;
    }
  },[html,value]);

  useEffect(() => {
    const updateSelection = () => {
      const selection = window.getSelection();
      const element = editable.current;
      if (!element || !selection || !selection.rangeCount || !element.contains(selection.anchorNode) || !element.contains(selection.focusNode)) {
        setToolbar(null); return;
      }
      range.current = selection.getRangeAt(0).cloneRange();
      if (selection.isCollapsed || !selection.toString().trim()) {setToolbar(null);return;}
      const rect = range.current.getBoundingClientRect();
      const toolbarWidth = 142;
      const left = Math.max(8,Math.min(window.innerWidth-toolbarWidth-8,rect.left+(rect.width-toolbarWidth)/2));
      const top = rect.top >= 58 ? rect.top-48 : rect.bottom+8;
      setToolbar({left,top:Math.max(8,Math.min(window.innerHeight-48,top)),bold:document.queryCommandState("bold"),italic:document.queryCommandState("italic"),underline:document.queryCommandState("underline")});
    };
    document.addEventListener("selectionchange",updateSelection);
    window.addEventListener("scroll",updateSelection,true);
    window.addEventListener("resize",updateSelection);
    return () => {
      document.removeEventListener("selectionchange",updateSelection);
      window.removeEventListener("scroll",updateSelection,true);
      window.removeEventListener("resize",updateSelection);
    };
  },[]);

  function publish() {
    if (!editable.current) return;
    const clean = sanitizeNewsletterHtml(editable.current.innerHTML);
    change.current(newsletterHtmlToText(clean),clean);
  }
  function format(command:Format) {
    const element = editable.current;
    if (!element) return;
    element.focus({preventScroll:true});
    const selection = window.getSelection();
    if (range.current && element.contains(range.current.commonAncestorContainer)) {
      selection?.removeAllRanges(); selection?.addRange(range.current);
    }
    document.execCommand("styleWithCSS",false,"false");
    document.execCommand(command,false);
    if (selection?.rangeCount) range.current = selection.getRangeAt(0).cloneRange();
    publish();
    document.dispatchEvent(new Event("selectionchange"));
  }

  return <div className={className} style={style}>
    <div ref={editable} className="newsletter-assistant-inline-editable newsletter-assistant-rich-editable" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={placeholder} data-placeholder={placeholder}
      onClick={event=>event.stopPropagation()}
      onInput={publish}
      onBlur={()=>{setToolbar(null);publish();}}
      onPaste={event=>{
        event.preventDefault();
        const clipboardHtml=event.clipboardData.getData("text/html");
        const clean=clipboardHtml ? sanitizeNewsletterHtml(clipboardHtml) : newsletterTextToHtml(event.clipboardData.getData("text/plain"));
        document.execCommand("insertHTML",false,clean);
        publish();
      }}
      onKeyDown={event=>{
        if ((event.metaKey||event.ctrlKey) && !event.altKey) {
          const command=({b:"bold",i:"italic",u:"underline"} as const)[event.key.toLowerCase() as "b"|"i"|"u"];
          if (command) {event.preventDefault();event.stopPropagation();format(command);}
        }
      }} />
    {toolbar && createPortal(<div className="newsletter-assistant-text-toolbar" role="toolbar" aria-label="Mise en forme du texte" style={{left:toolbar.left,top:toolbar.top}} onMouseDown={event=>event.preventDefault()}>
      {([['bold','Gras','G'],['italic','Italique','I'],['underline','Souligné','S']] as const).map(([command,label,glyph])=><button key={command} type="button" aria-label={label} aria-pressed={toolbar[command]} title={`${label} (⌘/Ctrl + ${command==='bold'?'B':command==='italic'?'I':'U'})`} className={`format-${command}`} onClick={()=>format(command)}>{glyph}</button>)}
    </div>,document.body)}
  </div>;
}
